import { Component, Fragment, type ReactNode } from "react";
import { focusRouteHeading } from "./focus-management";
import { publishPortalRenderFailure } from "./portal-error-reporting";
import {
  createPortalBoundaryState,
  derivePortalNavigationReset,
  PORTAL_ROUTE_BOUNDARY_ID,
  retryPortalBoundary,
  type PortalBoundaryState,
  type PortalRenderFailureReporter,
  type PortalRouteId,
} from "./route-error-model";

interface PortalRouteErrorBoundaryProps {
  readonly routeId: PortalRouteId;
  readonly navigationEpoch: number;
  readonly reporter?: PortalRenderFailureReporter;
  readonly children: ReactNode;
}

interface PortalRouteErrorFallbackProps {
  readonly onRetry: () => void;
}

export function PortalRouteErrorFallback({
  onRetry,
}: PortalRouteErrorFallbackProps) {
  return (
    <section
      className="portal-route-error"
      aria-labelledby="portal-title"
      aria-describedby="portal-route-error-detail"
    >
      <div className="portal-error-card">
        <p className="portal-kicker">Secure portal</p>
        <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
          This page needs another try.
        </h1>
        <p id="portal-route-error-detail">
          Your private information is safe. Try opening this page again or
          return to portal home.
        </p>
        <div className="portal-error-actions">
          <button type="button" onClick={onRetry}>
            Try again
          </button>
          <a href="/">Return to portal home</a>
        </div>
      </div>
    </section>
  );
}

export class PortalRouteErrorBoundary extends Component<
  PortalRouteErrorBoundaryProps,
  PortalBoundaryState
> {
  override state = createPortalBoundaryState(0);

  static getDerivedStateFromProps(
    props: PortalRouteErrorBoundaryProps,
    state: PortalBoundaryState,
  ) {
    return derivePortalNavigationReset(state, props.navigationEpoch);
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch() {
    publishPortalRenderFailure(
      {
        code: "PORTAL_ROUTE_RENDER_FAILED",
        boundaryId: PORTAL_ROUTE_BOUNDARY_ID,
        routeId: this.props.routeId,
        retryCount: this.state.retryCount,
      },
      this.props.reporter,
    );
    this.scheduleHeadingFocus();
  }

  private pendingFocusFrame: number | undefined;

  private readonly scheduleHeadingFocus = () => {
    if (typeof window === "undefined") return;
    if (this.pendingFocusFrame !== undefined) {
      window.cancelAnimationFrame(this.pendingFocusFrame);
    }
    this.pendingFocusFrame = window.requestAnimationFrame(() => {
      this.pendingFocusFrame = undefined;
      focusRouteHeading();
    });
  };

  private readonly retryRoute = () => {
    this.setState(
      (state) => retryPortalBoundary(state),
      this.scheduleHeadingFocus,
    );
  };

  override componentWillUnmount() {
    if (typeof window !== "undefined" && this.pendingFocusFrame !== undefined) {
      window.cancelAnimationFrame(this.pendingFocusFrame);
    }
  }

  override render() {
    if (this.state.failed) {
      return <PortalRouteErrorFallback onRetry={this.retryRoute} />;
    }

    return (
      <Fragment
        key={`${this.state.navigationEpoch}:${this.state.remountSequence}`}
      >
        {this.props.children}
      </Fragment>
    );
  }
}
